/**
 * Billing (mocked provider): snapshot, quote previews, changes, keep-plan,
 * trial actions, payment-method portal and invoices. Every response is
 * checked against the contract before it is shown; a mismatch is an error,
 * never a guessed value. Money is integer pence.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from './http'
import { keys } from './queries'
import { userSettingsKey } from './settings'

export type BillingBasePlan = 'home-buyer' | 'investor'
export type BillingAddon = 'hmo-addon' | 'serviced-accommodation-addon'
export interface BillingLine { description: string; amountPence: number }
export interface BillingInvoice {
  id: string; number: string; createdAt: string; amountPence: number; status: 'paid'
  description: string; hostedInvoiceUrl: string; lines: BillingLine[]
}
export interface BillingScheduledChange {
  basePlan: BillingBasePlan | null; addons: BillingAddon[]; effectiveAt: string; nextTotalPence: number
}
export interface BillingTrial {
  startedAt: string; endsAt: string; reconciledAt: string | null; cancelAtEnd: boolean; paymentMethodConfirmed: true
  afterTrialBasePlan: BillingBasePlan; afterTrialAddons: BillingAddon[]; monthlyTotalPence: number
}
export interface BillingCatalogItem { code: BillingBasePlan | BillingAddon; name: string; monthlyPricePence: number; kind: 'base' | 'addon'; requiresBasePlan: 'investor' | null; featureAvailable: boolean }
export interface BillingSnapshot {
  provider: 'mock'; currency: 'gbp'; samplePrices: true; revision: number
  accessState: 'unpaid' | 'trial' | 'homeBuyer' | 'investor'; trialEndsAt: string | null
  trialEligible: boolean; trialDurationDays: 2; trial: BillingTrial | null
  subscription: null | { basePlan: BillingBasePlan; addons: BillingAddon[]; periodStart: string; periodEnd: string; monthlyTotalPence: number; state: 'active' }
  scheduledChange: BillingScheduledChange | null
  catalog: BillingCatalogItem[]
  invoices: BillingInvoice[]
}
export interface BillingQuote {
  changeKind: 'subscription' | 'trial-renewal'
  id: string; revision: number; expiresAt: string; dueNowPence: number; nextTotalPence: number
  effectiveAt: string; immediateBasePlan: BillingBasePlan | null; immediateAddons: BillingAddon[]
  scheduledChange: BillingScheduledChange | null; lines: BillingLine[]
}
export interface BillingSelection { expectedRevision: number; basePlan: BillingBasePlan | null; addons: BillingAddon[] }
export type TrialAction = 'start' | 'cancel' | 'resume'

export const BILLING_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
function mismatch(): never { throw new ApiError(502, 'billing_contract_mismatch', 'Billing details could not be verified. Please refresh and try again.') }
function object(value: unknown): Record<string, unknown> { if (!value || typeof value !== 'object' || Array.isArray(value)) mismatch(); return value as Record<string, unknown> }
function string(value: unknown): string { if (typeof value !== 'string' || !value.trim()) mismatch(); return value }
function integer(value: unknown, signed = false): number { if (typeof value !== 'number' || !Number.isSafeInteger(value) || (!signed && value < 0)) mismatch(); return value }
function timestamp(value: unknown): string { const result = string(value); if (!/^\d{4}-\d{2}-\d{2}T/.test(result) || Number.isNaN(Date.parse(result))) mismatch(); return result }
function uuid(value: unknown): string { const result = string(value); if (!BILLING_UUID.test(result)) mismatch(); return result }
function base(value: unknown): BillingBasePlan { if (value !== 'home-buyer' && value !== 'investor') mismatch(); return value }
function addons(value: unknown): BillingAddon[] {
  if (!Array.isArray(value) || value.some(item => item !== 'hmo-addon' && item !== 'serviced-accommodation-addon') || new Set(value).size !== value.length) mismatch()
  return [...value] as BillingAddon[]
}
function lines(value: unknown): BillingLine[] {
  if (!Array.isArray(value)) mismatch()
  return value.map(item => { const row = object(item); return { description: string(row.description), amountPence: integer(row.amountPence, true) } })
}
function validateLineTotal(items: BillingLine[], total: number) {
  let sum = 0
  for (const item of items) { sum += item.amountPence; if (!Number.isSafeInteger(sum)) mismatch() }
  if (sum !== total) mismatch()
}
function selectionInvariant(plan: BillingBasePlan | null, selected: BillingAddon[]) { if (plan !== 'investor' && selected.length) mismatch() }
function scheduled(value: unknown): BillingScheduledChange | null {
  if (value === null) return null
  const row = object(value)
  const result = { basePlan: row.basePlan === null ? null : base(row.basePlan), addons: addons(row.addons), effectiveAt: timestamp(row.effectiveAt), nextTotalPence: integer(row.nextTotalPence) }
  selectionInvariant(result.basePlan, result.addons)
  return result
}
function trial(value: unknown): BillingTrial | null {
  if (value === null) return null
  const row = object(value)
  if (typeof row.cancelAtEnd !== 'boolean' || row.paymentMethodConfirmed !== true) mismatch()
  const result: BillingTrial = {
    startedAt: timestamp(row.startedAt), endsAt: timestamp(row.endsAt), reconciledAt: row.reconciledAt === null ? null : timestamp(row.reconciledAt), cancelAtEnd: row.cancelAtEnd,
    paymentMethodConfirmed: true, afterTrialBasePlan: base(row.afterTrialBasePlan),
    afterTrialAddons: addons(row.afterTrialAddons), monthlyTotalPence: integer(row.monthlyTotalPence),
  }
  if (Date.parse(result.endsAt) - Date.parse(result.startedAt) !== 2 * 86400000) mismatch()
  if (result.reconciledAt !== null && Date.parse(result.reconciledAt) < Date.parse(result.endsAt)) mismatch()
  selectionInvariant(result.afterTrialBasePlan, result.afterTrialAddons)
  return result
}
function invoice(value: unknown): BillingInvoice {
  const row = object(value); const id = uuid(row.id)
  if (row.status !== 'paid' || row.hostedInvoiceUrl !== `/billing/invoices/${id}`) mismatch()
  const result = { id, number: string(row.number), createdAt: timestamp(row.createdAt), amountPence: integer(row.amountPence), status: 'paid' as const, description: string(row.description), hostedInvoiceUrl: row.hostedInvoiceUrl as string, lines: lines(row.lines) }
  validateLineTotal(result.lines, result.amountPence)
  return result
}

export function parseBillingInvoiceEnvelope(value: unknown): BillingInvoice { return invoice(object(value).data) }

export function parseBillingSnapshotEnvelope(value: unknown): BillingSnapshot {
  const row = object(object(value).data)
  if (row.provider !== 'mock' || row.currency !== 'gbp' || row.samplePrices !== true || !['unpaid', 'trial', 'homeBuyer', 'investor'].includes(String(row.accessState)) || typeof row.trialEligible !== 'boolean' || row.trialDurationDays !== 2) mismatch()
  const savedTrial = trial(row.trial)
  if (savedTrial && row.trialEligible) mismatch()
  let subscription: BillingSnapshot['subscription'] = null
  if (row.subscription !== null) {
    const sub = object(row.subscription)
    if (sub.state !== 'active') mismatch()
    subscription = { basePlan: base(sub.basePlan), addons: addons(sub.addons), periodStart: timestamp(sub.periodStart), periodEnd: timestamp(sub.periodEnd), monthlyTotalPence: integer(sub.monthlyTotalPence), state: 'active' }
    selectionInvariant(subscription.basePlan, subscription.addons)
    if (Date.parse(subscription.periodEnd) <= Date.parse(subscription.periodStart)) mismatch()
  }
  if (!Array.isArray(row.catalog) || !Array.isArray(row.invoices)) mismatch()
  const catalog = row.catalog.map((value): BillingCatalogItem => {
    const item = object(value)
    if (!['home-buyer', 'investor', 'hmo-addon', 'serviced-accommodation-addon'].includes(String(item.code)) || typeof item.featureAvailable !== 'boolean') mismatch()
    const code = item.code as BillingBasePlan | BillingAddon
    const kind: 'base' | 'addon' = code === 'home-buyer' || code === 'investor' ? 'base' : 'addon'
    if (item.kind !== kind || item.requiresBasePlan !== (kind === 'addon' ? 'investor' : null)) mismatch()
    return { code, name: string(item.name), monthlyPricePence: integer(item.monthlyPricePence), kind, requiresBasePlan: item.requiresBasePlan as 'investor' | null, featureAvailable: item.featureAvailable as boolean }
  })
  if (new Set(catalog.map(item => item.code)).size !== catalog.length || catalog.length !== 4) mismatch()
  const trialEndsAt = row.trialEndsAt === null ? null : timestamp(row.trialEndsAt)
  if (row.accessState === 'trial' && savedTrial && trialEndsAt !== savedTrial.endsAt) mismatch()
  return { provider: 'mock', currency: 'gbp', samplePrices: true, revision: integer(row.revision), accessState: row.accessState as BillingSnapshot['accessState'], trialEndsAt, trialEligible: row.trialEligible as boolean, trialDurationDays: 2, trial: savedTrial, subscription, scheduledChange: scheduled(row.scheduledChange), catalog, invoices: row.invoices.map(invoice) }
}

export function parseBillingQuoteEnvelope(value: unknown): BillingQuote {
  const row = object(object(value).data)
  if (row.changeKind !== 'subscription' && row.changeKind !== 'trial-renewal') mismatch()
  const quote = { id: uuid(row.id), revision: integer(row.revision), expiresAt: timestamp(row.expiresAt), dueNowPence: integer(row.dueNowPence), nextTotalPence: integer(row.nextTotalPence), effectiveAt: timestamp(row.effectiveAt), immediateBasePlan: row.immediateBasePlan === null ? null : base(row.immediateBasePlan), immediateAddons: addons(row.immediateAddons), scheduledChange: scheduled(row.scheduledChange), lines: lines(row.lines) }
  selectionInvariant(quote.immediateBasePlan, quote.immediateAddons)
  validateLineTotal(quote.lines, quote.dueNowPence)
  if (row.changeKind === 'trial-renewal' && (quote.dueNowPence !== 0 || quote.immediateBasePlan !== null || quote.immediateAddons.length !== 0 || quote.lines.length !== 0 || !quote.scheduledChange?.basePlan || quote.scheduledChange.effectiveAt !== quote.effectiveAt || quote.scheduledChange.nextTotalPence !== quote.nextTotalPence)) mismatch()
  return { ...quote, changeKind: row.changeKind }
}

export const billingKeys = {
  snapshot: ['billing'] as const,
  invoice: (id: string) => ['billingInvoice', id] as const,
}

/** What a membership change can alter: access, the experience, and every saved result view. */
async function afterMembershipChange(client: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    client.invalidateQueries({ queryKey: keys.entitlements }),
    client.invalidateQueries({ queryKey: keys.mode }),
    ...[['calculationCards'], ['propertyMarkers'], ['property'], userSettingsKey, ['dealsV2']].map(queryKey => client.invalidateQueries({ queryKey, refetchType: 'none' })),
  ])
}

export function useBilling() {
  return useQuery({ queryKey: billingKeys.snapshot, queryFn: async ({ signal }) => parseBillingSnapshotEnvelope(await api('billing', { signal })), staleTime: 30_000, retry: false })
}

export function useBillingInvoice(id: string) {
  return useQuery({ queryKey: billingKeys.invoice(id), enabled: BILLING_UUID.test(id), queryFn: async ({ signal }) => parseBillingInvoiceEnvelope(await api('billingInvoice', { params: { invoiceId: id }, signal })), retry: false })
}

/** A quote for a selection. The server keeps it briefly so a change can apply exactly that amount. */
export function useBillingPreview() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (selection: BillingSelection) => parseBillingQuoteEnvelope(await api('billingPreview', { body: selection })),
    onError: async error => { if (error instanceof ApiError && error.status === 409) await client.invalidateQueries({ queryKey: billingKeys.snapshot }) },
  })
}

/** Apply a quoted change, or (`keep`) keep the current plan and drop a scheduled change. */
export function useBillingChange(keep = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { expectedRevision: number; idempotencyKey: string; quoteId?: string }) => parseBillingSnapshotEnvelope(await api(keep ? 'billingKeep' : 'billingChange', { body: input })),
    onSuccess: async snapshot => { client.setQueryData(billingKeys.snapshot, snapshot); await afterMembershipChange(client) },
    onError: error => { if (error instanceof ApiError && error.status === 409) void client.invalidateQueries({ queryKey: billingKeys.snapshot }) },
  })
}

export function useBillingTrial() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { action: TrialAction; expectedRevision: number; idempotencyKey: string }) => parseBillingSnapshotEnvelope(await api('billingTrial', { body: input })),
    onSuccess: async snapshot => { client.setQueryData(billingKeys.snapshot, snapshot); await afterMembershipChange(client) },
    onError: error => { if (error instanceof ApiError && error.status === 409) void client.invalidateQueries({ queryKey: billingKeys.snapshot }) },
  })
}

/** The mock provider's payment-method portal: no card details exist to manage. */
export function useBillingPortal() {
  return useMutation({
    mutationFn: async () => {
      const row = object(object(await api('billingPortal')).data)
      if (row.provider !== 'mock' || row.url !== '/billing/portal') mismatch()
      return { provider: 'mock' as const, url: row.url as '/billing/portal' }
    },
  })
}
